// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  anularLicencia,
  crearLicencia,
  traerLicencias,
  estadoDeLicencia,
  rotuloDeEstado,
  cargadaPor,
} from "../services/licencias";
import { traerChoferes } from "../services/rrhh";
import {
  GRUPOS_QUE_SE_CARGAN,
  pastillaDeLicencia,
  rotuloDeLicencia,
} from "../shared/licenciaTipos";
import { rutaDePersona } from "../shared/personas";
import { hoyAr } from "../shared/fechaAr";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Licencias: partes de enfermo, vacaciones, francos e inasistencias,
 * cargados por RRHH.
 *
 * ── QUÉ RESUELVE ─────────────────────────────────────────────────────────────
 *
 * Quién no está trabajando y por qué no vivía en ningún lado del sistema: el
 * certificado quedaba en un mail y las vacaciones en la cabeza de RRHH. Esta
 * pantalla es LA fuente: lo que se carga acá aparece en la ficha de la
 * persona (las tarjetas de Vacaciones y Partes de enfermo) y en el patio del
 * bot — la enfermería y la laguna. El bot escribe la MISMA tabla con
 * `/licencia`, así que las dos puertas se ven entre sí.
 *
 * ── DOS LLAVES, LAS DE LA NÓMINA ─────────────────────────────────────────────
 *
 * Se mira con `ver.rrhh` y se carga con `cargar.rrhh` — y la pared es la
 * base, no esta pantalla: sin la primera, la consulta vuelve vacía; sin la
 * segunda, el insert rebota. Hoy las dos las tienen Vilte y Erick.
 *
 * ── LOS QUE SE CARGAN, AGRUPADOS COMO LOS PIENSA PERSONAL ────────────────────
 *
 * El formulario ofrece Salud (enfermo, ART), Descanso (vacaciones, franco) e
 * Inasistencias: la lista que mandaron Vilte y Erick el 08/10/2026, con el
 * nombre que usan ellos. La tabla lee además los que trajo el histórico de
 * ausentismo y Personal no pidió cargar —`shared/licenciaTipos` los tiene a
 * todos—: hasta que existió esa tabla, cualquier tipo que no fuera «enfermo»
 * se dibujaba como «🏖️ vacaciones», que no era un rótulo feo sino uno falso.
 *
 * El franco está en el formulario por un motivo concreto: el banco de francos
 * del bot (`/banco-de-francos/uat`) mide lo que se gana viajando contra lo que
 * se toma, y lo tomado sólo existe si alguien lo carga. Sin esta opción, ese
 * lado de la cuenta daba cero para siempre.
 *
 * ── ANULAR NO BORRA ──────────────────────────────────────────────────────────
 *
 * La fila queda, con quién la anuló y cuándo. En el historial de la persona
 * una licencia anulada es historia, no ruido.
 */

const HOY = () => hoyAr(); // el «hoy» argentino, no el de UTC (fechaAr.js)

const fecha = (v) => {
  const s = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [a, m, d] = s.split("-");
  return `${d}/${m}/${a}`;
};

/* Los que se ofrecen para cargar, por grupo. La tabla completa —y el rótulo
 * de los que sólo se leen— vive en `shared/licenciaTipos`. */
const GRUPOS = GRUPOS_QUE_SE_CARGAN.map(({ titulo, tipos }) => ({
  titulo,
  tipos: tipos.map((id) => ({ id, ...rotuloDeLicencia(id) })),
}));

const Licencias = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);
  const puedeCargar = puede("cargar.rrhh");

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [choferes, setChoferes] = useState([]);

  // El formulario. `hasta` es lo único sin default: es EL dato — la fecha
  // de alta del certificado o la vuelta de las vacaciones.
  const [dni, setDni] = useState("");
  const [buscar, setBuscar] = useState("");
  const [tipo, setTipo] = useState("enfermo");
  const [desde, setDesde] = useState(HOY());
  const [hasta, setHasta] = useState("");
  const [nota, setNota] = useState("");
  const [estado, setEstado] = useState(""); // "" | yendo | mal:<msg>

  const permitido = permisos !== null && permisos.includes("ver.rrhh");

  const cargarTodo = () => {
    traerLicencias()
      .then(setFilas)
      .catch(() => setError(true));
  };

  useEffect(() => {
    if (!permitido) return;
    cargarTodo();
    // Los choferes de la nómina activa, para elegir sin tipear DNI. Si no
    // cargan, el DNI a mano sigue andando: es un atajo, no una puerta.
    traerChoferes()
      .then(setChoferes)
      .catch(() => setChoferes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitido]);

  const nombreDe = useMemo(() => {
    const m = new Map();
    for (const c of choferes)
      m.set(String(c.dni), `${c.apellido}, ${c.nombres}`);
    return m;
  }, [choferes]);

  const candidatos = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (q.length < 2) return [];
    return choferes
      .filter((c) =>
        [c.apellido, c.nombres, c.dni, c.legajo]
          .map((v) => String(v ?? "").toLowerCase())
          .some((v) => v.includes(q)),
      )
      .slice(0, 8);
  }, [choferes, buscar]);

  const guardar = async (e) => {
    e.preventDefault();
    const d = dni.replace(/\D/g, "");
    if (!d) return setEstado("mal:Falta la persona (elegila o escribí el DNI).");
    if (!hasta) return setEstado("mal:Falta el «hasta» — es el dato.");
    if (hasta < desde)
      return setEstado(
        "mal:El período está al revés: termina antes de empezar.",
      );
    setEstado("yendo");
    try {
      await crearLicencia({ dni: d, tipo, desde, hasta, nota });
      setEstado("");
      setDni("");
      setBuscar("");
      setHasta("");
      setNota("");
      setDesde(HOY());
      cargarTodo();
    } catch (err) {
      // El rebote más probable es la llave: sin `cargar.rrhh` la base
      // rechaza el insert, y eso se dice como lo que es.
      setEstado(
        "mal:" +
          (/policy|permission|denied|violates/i.test(err?.message || "")
            ? "No se cargó: no tenés permiso."
            : `No se pudo guardar: ${err?.message || "error"}`),
      );
    }
  };

  const anular = async (id) => {
    try {
      await anularLicencia(id);
      cargarTodo();
    } catch {
      setEstado("mal:No se pudo anular. Probá de nuevo en un rato.");
    }
  };

  if (!puede("ver.rrhh")) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  const lista = filas || [];
  const hoy = HOY();

  return (
    <>
      {/* ── La carga ──────────────────────────────────────────────────────
          Sólo se dibuja con la llave de escritura: un formulario que la base
          va a rebotar es una promesa rota en diferido. */}
      {puedeCargar && (
        <form className="tk-filtros" onSubmit={guardar} style={{ rowGap: 8 }}>
          <div style={{ position: "relative" }}>
            <input
              type="search"
              placeholder="Persona (nombre, DNI o legajo)"
              value={buscar}
              onChange={(e) => {
                setBuscar(e.target.value);
                setDni("");
              }}
              aria-label="Buscar a la persona"
            />
            {/* El desplegable de candidatos: elegir fija el DNI. Si la
                nómina no lo trae, el DNI escrito a mano vale igual. */}
            {!dni && candidatos.length > 0 && (
              <div className="lic-sugeridos">
                {candidatos.map((c) => (
                  <button
                    key={c.dni}
                    type="button"
                    onClick={() => {
                      setDni(String(c.dni));
                      setBuscar(`${c.apellido}, ${c.nombres}`);
                    }}
                  >
                    {c.apellido}, {c.nombres}{" "}
                    <span className="sub">
                      DNI {c.dni} · {c.destino || "sin base"}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!dni && /^\d{6,9}$/.test(buscar.replace(/\D/g, "")) &&
              candidatos.length === 0 && (
                <button
                  type="button"
                  className="tk-chip"
                  onClick={() => setDni(buscar.replace(/\D/g, ""))}
                >
                  Usar el DNI {buscar.replace(/\D/g, "")}
                </button>
              )}
          </div>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            aria-label="Tipo de licencia"
          >
            {GRUPOS.map((g) => (
              <optgroup key={g.titulo} label={g.titulo}>
                {g.tipos.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.emoji} {t.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <label className="sub">
            desde{" "}
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </label>
          <label className="sub">
            {tipo === "enfermo" ? "alta (fin del certificado)" : "hasta"}{" "}
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>
          <input
            type="text"
            placeholder="Nota (opcional)"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            aria-label="Nota"
          />
          <button
            type="submit"
            className="btn-primario"
            disabled={estado === "yendo"}
          >
            {estado === "yendo" ? "Guardando…" : "Cargar"}
          </button>
          {estado.startsWith("mal:") && (
            <span className="pill mal">{estado.slice(4)}</span>
          )}
        </form>
      )}

      {!puedeCargar && (
        <p className="panel-aviso suave">
          Podés mirar, pero no cargar.
        </p>
      )}

      {filas !== undefined && lista.length === 0 && (
        <div className="panel-aviso suave">
          <h3>No hay ninguna licencia cargada</h3>
        </div>
      )}

      {lista.length > 0 && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Persona</th>
                <th>Tipo</th>
                <th>Período</th>
                <th>Nota</th>
                <th>Cargó</th>
                {puedeCargar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {lista.map((l) => {
                const est = estadoDeLicencia(l, hoy);
                const vig = est === "en_curso";
                const rotEst = rotuloDeEstado(est, l, fecha);
                const vencidaEnfermo =
                  !l.anulado_en && l.tipo === "enfermo" && l.hasta < hoy;
                return (
                  <tr key={l.id} className={l.anulado_en ? "tenue" : ""}>
                    <td>
                      {(() => {
                        /* El nombre está en la fila y se usa. El padrón sólo
                           lo mejora cuando hay DNI —trae «APELLIDO, NOMBRES»
                           al día—; el legajo es el último recurso ANTES de
                           mostrar un identificador vacío. */
                        const quien =
                          nombreDe.get(String(l.dni)) ||
                          l.nombre ||
                          (l.legajo ? `Legajo ${l.legajo}` : null) ||
                          (l.dni ? `DNI ${l.dni}` : "sin identificar");
                        const ruta = rutaDePersona(l.dni);
                        return ruta ? (
                          <Link className="rto-link" to={ruta}>
                            {quien}
                          </Link>
                        ) : (
                          /* Sin DNI no hay ficha que consultar: se muestra el
                             nombre, no un enlace que no lleva a ningún lado. */
                          <span>{quien}</span>
                        );
                      })()}
                    </td>
                    <td>
                      <span
                        className={
                          "pill " +
                          pastillaDeLicencia(l.tipo, {
                            vigente: vig,
                            anulada: Boolean(l.anulado_en),
                          })
                        }
                      >
                        {rotuloDeLicencia(l.tipo).emoji}{" "}
                        {rotuloDeLicencia(l.tipo).label}
                      </span>
                      {l.anulado_en ? (
                        <span
                          className="sub"
                          title={`La anuló ${l.anulado_por || "?"}`}
                        >
                          {" "}
                          anulada
                        </span>
                      ) : (
                        /* Lo que todavía no empezó no está terminado, y lo que
                           no tiene fin no está corriendo: las dos cosas se
                           dicen, en vez de dejarlas caer en el gris. */
                        est !== "en_curso" && (
                          <span className={"pill " + rotEst.pill}>
                            {" "}
                            {rotEst.texto}
                          </span>
                        )
                      )}
                      {/* El alta vencida es una PREGUNTA, no un dato viejo:
                          ¿volvió, o hay que renovar el certificado? */}
                      {vencidaEnfermo && (
                        <span className="pill ojo">
                          alta vencida, sin novedad
                        </span>
                      )}
                    </td>
                    <td className="dato">
                      {fecha(l.desde)} → {fecha(l.hasta)}
                      {l.tipo === "enfermo" && !l.anulado_en && (
                        <span className="sub"> · alta {fecha(l.hasta)}</span>
                      )}
                    </td>
                    <td className="sub">{l.nota || "—"}</td>
                    <td className="sub">{cargadaPor(l)}</td>
                    {puedeCargar && (
                      <td>
                        {!l.anulado_en && (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => anular(l.id)}
                          >
                            Anular
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </>
  );
};

export default Licencias;
